import Quagga, { QuaggaJSConfigObject } from '@ericblade/quagga2';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';

interface SampleBarcodeScannerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDetected: (barcode: string) => void;
}

function SampleBarcodeScannerDialog({ isOpen, onClose, onDetected }: SampleBarcodeScannerDialogProps) {
  const [scannerElement, setScannerElement] = useState<HTMLDivElement | null>(null);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [scannerStatus, setScannerStatus] = useState('Starting camera...');
  const [manualBarcode, setManualBarcode] = useState('');

  const setScannerRef = useCallback((element: HTMLDivElement | null) => {
    setScannerElement(element);
  }, []);

  const stopScanner = useCallback(() => {
    try {
      Quagga.stop();
    } catch {
      // Quagga may already be stopped.
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !scannerElement) {
      return undefined;
    }

    let isMounted = true;

    const handleDetected = (result: any) => {
      const barcode = result?.codeResult?.code;

      if (!barcode) {
        return;
      }

      onDetected(barcode);

      try {
        Quagga.offDetected(handleDetected);
        Quagga.stop();
      } catch {
        // Quagga may already be stopped.
      }

      onClose();
    };

    const startScanner = () => {
      setScannerError(null);
      setScannerStatus('Checking camera access...');

      if (!window.isSecureContext) {
        setScannerError(
          'Camera access requires HTTPS. Localhost works on a development machine, but tablets must use a secure HTTPS URL.',
        );
        setScannerStatus('Camera unavailable');
        return;
      }

      const config: QuaggaJSConfigObject = {
        inputStream: {
          name: 'Live',
          type: 'LiveStream',
          target: scannerElement,
          constraints: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        locate: true,
        numOfWorkers: 0,
        frequency: 10,
        locator: {
          patchSize: 'medium',
          halfSample: true,
        },
        decoder: {
          readers: ['code_128_reader'],
        },
      };

      setScannerStatus('Starting camera stream...');

      Quagga.init(config, (error) => {
        if (!isMounted) {
          return;
        }

        if (error) {
          setScannerError(
            error instanceof Error
              ? error.message
              : 'The camera scanner could not be started. Please check camera permissions or enter the barcode manually.',
          );
          setScannerStatus('Camera unavailable');
          return;
        }

        Quagga.onDetected(handleDetected);
        Quagga.start();

        setScannerStatus('Camera ready. Place the barcode inside the scanner area.');
      });
    };

    startScanner();

    return () => {
      isMounted = false;

      try {
        Quagga.offDetected(handleDetected);
        Quagga.stop();
      } catch {
        // Quagga may already be stopped.
      }
    };
  }, [isOpen, scannerElement, onClose, onDetected, stopScanner]);

  const handleUseManualBarcode = () => {
    const barcode = manualBarcode.trim();

    if (!barcode) {
      return;
    }

    onDetected(barcode);
    setManualBarcode('');
    stopScanner();
    onClose();
  };

  const handleClose = () => {
    setManualBarcode('');
    setScannerError(null);
    setScannerStatus('Starting camera...');
    stopScanner();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      keepMounted={false}
      TransitionProps={{
        onEntered: () => {
          setScannerStatus('Preparing camera...');
        },
        onExited: () => {
          stopScanner();
          setScannerElement(null);
          setScannerError(null);
          setScannerStatus('Starting camera...');
          setManualBarcode('');
        },
      }}
    >
      <DialogTitle>Scan Sample Kit Barcode</DialogTitle>

      <DialogContent>
        <Typography variant="body1" component="p" color="text.secondary" sx={{ mb: 2 }}>
          Point the tablet camera at the barcode on the sample kit. Hold the barcode steady and ensure it is well lit.
        </Typography>

        {scannerError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {scannerError}
          </Alert>
        )}

        {!scannerError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {scannerStatus}
          </Alert>
        )}

        <Box
          ref={setScannerRef}
          sx={{
            width: '100%',
            height: { xs: 320, md: 420 },
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.900',
            border: 1,
            borderColor: 'divider',
            position: 'relative',
            '& video': {
              width: '100% !important',
              height: '100% !important',
              objectFit: 'cover',
              display: 'block',
            },
            '& canvas': {
              display: 'none',
            },
          }}
        />

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            Enter barcode manually
          </Typography>

          <TextField
            label="Sample kit barcode"
            value={manualBarcode}
            onChange={(event) => setManualBarcode(event.target.value)}
            fullWidth
          />

          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={!manualBarcode.trim()}
            onClick={handleUseManualBarcode}
            sx={{ mt: 2, minHeight: 56, fontWeight: 700 }}
          >
            Use entered barcode
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SampleBarcodeScannerDialog;
